import React, { lazy, Suspense, useEffect } from "react";
import { useSelector } from "react-redux";
import { Switch, BrowserRouter as Router } from "react-router-dom";
import { connect } from "react-redux";

// Import Routes
import { authProtectedRoutes, publicRoutes } from "./routes/";
import AppRoute from "./routes/route";

// layouts
import VerticalLayout from "./components/VerticalLayout/";
import HorizontalLayout from "./components/HorizontalLayout/";
import NonAuthLayout from "./components/NonAuthLayout";

import SignalRControl from "./components/SignalRControl";
import Routines from "./components/Routines";

import "./workers/periods";
import ModalLogoutUserInfo from "./components/Common/ModalLogoutUserInfo";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { ReCaptchaVisibility } from "./contexts/ReCaptchaVisibility";
import { AutomaticSummarizationProvider } from "./providers/AutomaticSummarizationProvider";

const ThemeLight = lazy(() => import("./ThemeLight"));
const ThemeDark = lazy(() => import("./ThemeDark"));

const App = () => {
  const mainTheme = useSelector((state) => state.Layout.mainTheme);
  const oldTheme = useSelector((state) => state.Layout.oldTheme);

  useEffect(() => {
    if (oldTheme && oldTheme === "dark" && mainTheme === "light") {
      window.location.reload(false);
    }
  }, [mainTheme, oldTheme]);

  /**
   * Returns the layout
   */
  function getLayout(layoutType) {
    let layoutCls = VerticalLayout;

    switch (layoutType) {
      case "horizontal":
        layoutCls = HorizontalLayout;
        break;
      default:
        layoutCls = VerticalLayout;
        break;
    }
    return layoutCls;
  }

  const Layout = getLayout();

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY}
      container={{
        element: ReCaptchaVisibility.ref,
        parameters: { badge: "bottomright" },
      }}
    >
      <AutomaticSummarizationProvider>
        <ReCaptchaVisibility />
        <Suspense
          fallback={
            <div id="preloaderTheme" style={{ display: "block" }}>
              <div id="statusTheme" style={{ display: "block" }}>
                <div className="spinner-chase">
                  <div className="chase-dot"></div>
                  <div className="chase-dot"></div>
                  <div className="chase-dot"></div>
                  <div className="chase-dot"></div>
                  <div className="chase-dot"></div>
                  <div className="chase-dot"></div>
                </div>
              </div>
            </div>
          }
        >
          {mainTheme && mainTheme === "dark" ? <ThemeDark /> : <ThemeLight />}
        </Suspense>
        <SignalRControl />
        <ModalLogoutUserInfo />
        <Routines />
        <Router>
          <Switch>
            {publicRoutes.map((route, idx) => (
              <AppRoute
                path={route.path}
                layout={NonAuthLayout}
                component={route.component}
                key={idx}
                isAuthProtected={false}
              />
            ))}

            {authProtectedRoutes.map((route, idx) => (
              <AppRoute
                path={route.path}
                layout={Layout}
                component={route.component}
                key={idx}
                isAuthProtected={true}
                isPermissionProtected={route.isPermissionProtected}
                permissionName={route.permissionName}
              />
            ))}
          </Switch>
        </Router>
      </AutomaticSummarizationProvider>
    </GoogleReCaptchaProvider>
  );
};

const mapStateToProps = (state) => {
  return {
    layout: state.Layout,
  };
};

export default connect(mapStateToProps, null)(App);
